import { CardColors, CardFooter, CardFooterItem } from "$components/card";
import { InvestigationIcon as RawInvestigationIcon } from "$components/icons";
import {
  CardHeader,
  PatientHistoryCard,
} from "$routes/patient/_lib/history-card";
import { CircularProgress, styled, Typography } from "@material-ui/core";
import { format } from "date-fns";
import type { FC } from "react";
import { useState } from "react";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

const InvestigationIcon = styled(RawInvestigationIcon)(({ theme }) => ({
  marginRight: theme.spacing(1),
  color: theme.palette.blue.main,
}));

const ThumbnailsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  // Negative margin to reduce excessive footer spacing from PatientHistoryCard's FooterWrapper
  marginBottom: theme.spacing(-3),
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: 16,
  fontWeight: 400,
  lineHeight: "normal",
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(1.125), // 9px gap in Figma
  margin: 0,
}));

// TODO: Consider extracting ThumbnailGallery as a reusable component
// that can be shared between imaging-card and imaging-gallery-modal (also size slightly different in Figma)
const ThumbnailsGrid = styled("div")(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.5), // 12px gap to match Figma's gap-3
  justifyContent: "flex-start",
  flexWrap: "wrap",
  paddingTop: 0,
}));

const ThumbnailContainer = styled("div")(({ theme }) => ({
  position: "relative",
  width: 64,
  height: 64,
  borderRadius: 4,
  cursor: "pointer",
  boxSizing: "border-box",
}));

const ThumbnailImage = styled("img")(({ theme }) => ({
  width: 64,
  height: 64,
  objectFit: "cover",
  borderRadius: 4,
  cursor: "pointer",
}));

const ThumbnailPlaceholder = styled("div")(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[200],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
}));

export type ImagingCardImage = {
  id: string;
  thumbnailUrl?: string;
  previewUrl?: string;
};

import { useHistoryUserDetailsQuery } from "$routes/patient/[:patientId]/_lib/__generated__/user-details.gql.gql";
import { isCrossOrgAccessError } from "$routes/patient/[:patientId]/_lib/is-cross-org-access-error";
import { ImagingGalleryModal } from "./imaging-gallery-modal";
import { StudyWorksheetDetails } from "./study-worksheet-details";
import type { ImagingStudyWithImages } from "./use-patient-imaging-studies";
import { useStudyWorksheets } from "./use-study-worksheets";

const userDisplayMessages = defineMessages({
  differentFacility: {
    id: "imaging-study-card.footer.by.differentFacility",
    defaultMessage: "A different facility",
  },
  unknown: {
    id: "imaging-study-card.footer.by.unknown",
    defaultMessage: "Unknown",
  },
});

export const ImagingStudyCard: FC<{
  study: ImagingStudyWithImages;
}> = ({ study: data }) => {
  const { formatMessage } = useIntl();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const { worksheets } = useStudyWorksheets(data.accessionNumber);

  const {
    data: userData,
    error,
    loading,
  } = useHistoryUserDetailsQuery({
    variables: { userId: data.meta.createdByUserId },
  });

  const Header = () => (
    <CardHeader
      color={CardColors.INVESTIGATION}
      category="Investigation"
      title={"Ultrasound"}
      icon={<InvestigationIcon />}
      id={data.id}
    />
  );

  const Footer = () => (
    <CardFooter>
      <CardFooterItem label="Received:">
        {format(new Date(data.meta.created), "d MMM yyyy")}
      </CardFooterItem>
      <CardFooterItem label="From:">External source</CardFooterItem>
      <CardFooterItem label="By:">
        {isCrossOrgAccessError(error)
          ? formatMessage(userDisplayMessages.differentFacility)
          : error
            ? formatMessage(userDisplayMessages.unknown)
            : userData?.user?.person?.firstname
              ? [userData.user.person.firstname, userData.user.person.lastname]
                  .filter(Boolean)
                  .join(" ")
              : formatMessage(userDisplayMessages.unknown)}
      </CardFooterItem>
    </CardFooter>
  );

  return (
    <>
      <PatientHistoryCard
        id={`imagingCard${data.id}`}
        color={CardColors.INVESTIGATION}
        defaultExpanded={true}
        HeaderComponent={Header}
        FooterComponent={Footer}
      >
        <ThumbnailsContainer>
          <SectionSubtitle>Images</SectionSubtitle>
          <ThumbnailsGrid>
            {data.images.map((image, index) => (
              <ThumbnailContainer key={image.id}>
                {image.thumbnailLoading ? (
                  <ThumbnailPlaceholder>
                    <CircularProgress size={24} />
                  </ThumbnailPlaceholder>
                ) : image.thumbnailUrl ? (
                  <ThumbnailImage
                    src={image.thumbnailUrl}
                    alt={`Imaging study ${image.id}`}
                    onClick={() => {
                      setSelectedImageIndex(index);
                      setIsModalOpen(true);
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <ThumbnailPlaceholder
                    onClick={() => {
                      setSelectedImageIndex(index);
                      setIsModalOpen(true);
                    }}
                  >
                    {/* Empty placeholder for failed loads */}
                  </ThumbnailPlaceholder>
                )}
              </ThumbnailContainer>
            ))}
          </ThumbnailsGrid>
          {worksheets.length > 0 && (
            <>
              <SectionSubtitle>
                <FormattedMessage
                  id="imaging-study-card.worksheet.heading"
                  defaultMessage="Scan details"
                />
              </SectionSubtitle>
              <StudyWorksheetDetails worksheets={worksheets} />
            </>
          )}
        </ThumbnailsContainer>
      </PatientHistoryCard>

      <ImagingGalleryModal
        open={isModalOpen}
        images={data.images}
        initialImageIndex={selectedImageIndex}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};
